import { useState } from "react";
import { Link } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  accountsApi,
  clientAddressesApi,
  type AccountSummaryDto,
  type CreateAccountRequest,
} from "@/lib/clients-accounts";
import { apiFetch } from "@/lib/api";
import { AccountStatusBadge, ClientPicker, type AccountStatusCode, type ClientPickerValue } from "@/components/clients-accounts";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailsDrawer } from "@/components/details-drawer";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Saved-view chips
// ---------------------------------------------------------------------------

type SavedView = {
  id: string;
  label: string;
  qs: string;
  disabled?: boolean;
  tooltip?: string;
};

const SAVED_VIEWS: SavedView[] = [
  { id: "all",     label: "All",           qs: "" },
  { id: "open",    label: "Open",          qs: "status=Open" },
  { id: "overdue", label: "Overdue",       qs: "status=Overdue&overdueOnly=true" },
  { id: "balance", label: "Balance owing", qs: "minBalance=0.01" },
  { id: "hold",    label: "On hold",       qs: "status=Hold", disabled: true, tooltip: "Available after Plan II" },
  { id: "closed",  label: "Closed",        qs: "status=Closed" },
];

// ---------------------------------------------------------------------------
// DTOs for companies
// ---------------------------------------------------------------------------

interface CompanyOption { id: number; companyName: string; }
interface ClientAddressOption { id: number; formattedAddress: string | null; address: string; }

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ---------------------------------------------------------------------------
// New Account drawer form
// ---------------------------------------------------------------------------

function NewAccountForm({
  onSubmit,
  pending,
  conflictMessage,
}: {
  onSubmit: (req: CreateAccountRequest) => void;
  pending: boolean;
  conflictMessage: string | null;
}) {
  const [client, setClient] = useState<ClientPickerValue | null>(null);
  const [companyId, setCompanyId] = useState<string>("");
  const [addressId, setAddressId] = useState<string>("");

  const { data: companies } = useQuery({
    queryKey: ["companies-list"],
    queryFn: () => apiFetch<{ items: CompanyOption[] }>("/api/companies?pageSize=500"),
    staleTime: 60_000,
  });

  const { data: addresses } = useQuery({
    queryKey: ["client-addresses-picker", client?.clientId],
    queryFn: () => clientAddressesApi.list(client!.clientId),
    enabled: client !== null,
  });

  // When client is selected, auto-fill company if the client has one
  function handleClientChange(v: ClientPickerValue | null) {
    setClient(v);
    setAddressId("");
    if (v?.companyId) {
      setCompanyId(String(v.companyId));
    } else {
      setCompanyId("");
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!client || !companyId || !addressId) return;
    onSubmit({
      clientId: client.clientId,
      companyId: Number(companyId),
      accountAddressId: Number(addressId),
    });
  }

  const activeAddresses = (addresses ?? []).filter((a) => !(a as ClientAddressOption & { deleted?: boolean }).deleted);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {conflictMessage && (
        <Badge variant="destructive" className="w-full text-wrap">{conflictMessage}</Badge>
      )}
      <div>
        <Label>Client *</Label>
        <ClientPicker value={client} onChange={handleClientChange} />
      </div>
      <div>
        <Label htmlFor="na-company">Company *</Label>
        <Select
          value={companyId}
          onValueChange={setCompanyId}
          disabled={!!(client?.companyId)}
        >
          <SelectTrigger id="na-company">
            <SelectValue placeholder="Select company" />
          </SelectTrigger>
          <SelectContent>
            {(companies?.items ?? []).map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>{c.companyName}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {client?.companyId && (
          <p className="mt-1 text-xs text-muted-foreground">Auto-filled from selected client</p>
        )}
      </div>
      <div>
        <Label htmlFor="na-address">Address *</Label>
        <Select
          value={addressId}
          onValueChange={setAddressId}
          disabled={!client}
        >
          <SelectTrigger id="na-address">
            <SelectValue placeholder={client ? "Select address" : "Select a client first"} />
          </SelectTrigger>
          <SelectContent>
            {activeAddresses.map((a: ClientAddressOption) => (
              <SelectItem key={a.id} value={String(a.id)}>
                {a.formattedAddress ?? a.address}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" disabled={pending || !client || !companyId || !addressId}>
        {pending ? "Creating…" : "Create account"}
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// CSV export (client-side, current page only)
// ---------------------------------------------------------------------------

function exportCsv(rows: AccountSummaryDto[]) {
  const headers = ["Ref", "Company", "Client", "Status", "Balance", "Overdue", "Last payment", "Last stmt", "Next due", "Terms accepted"];
  const lines = rows.map((r) => [
    r.accountReferenceNo,
    r.companyName ?? "",
    r.clientFullName ?? "",
    r.accountStatusCode,
    r.currentBalanceOutstanding,
    r.totalOverdue ?? 0,
    r.lastPaymentDate ?? "",
    r.lastStatementIssuedDate ?? "",
    r.nextPaymentDueDate ?? "",
    r.termsAccepted ? "Yes" : "No",
  ].map(String).join(","));
  const csv = [headers.join(","), ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "accounts.csv";
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// AccountsListPage
// ---------------------------------------------------------------------------

export function AccountsListPage() {
  const [activeView, setActiveView] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [creating, setCreating] = useState(false);
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);
  const qc = useQueryClient();

  const viewQs = SAVED_VIEWS.find((v) => v.id === activeView)?.qs ?? "";
  const fullQs = [`page=${page}&pageSize=50`, viewQs].filter(Boolean).join("&");

  const { data, isLoading } = useQuery({
    queryKey: ["accounts", page, activeView],
    queryFn: () => accountsApi.list(fullQs),
  });

  const createMut = useMutation({
    mutationFn: (req: CreateAccountRequest) => accountsApi.create(req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["accounts"] });
      setCreating(false);
      setConflictMessage(null);
      toast.success("Account created");
    },
    onError: async (err: unknown) => {
      // Try to extract a 409 problem-detail title
      if (err instanceof Response && err.status === 409) {
        try {
          const body = await err.json() as { title?: string };
          setConflictMessage(body.title ?? "Conflict — this client or company already has an account.");
        } catch {
          setConflictMessage("Conflict — this client or company already has an account.");
        }
      }
    },
  });

  const cols: DataGridColumn<AccountSummaryDto>[] = [
    {
      id: "ref",
      header: "Ref",
      accessor: (r) => (
        <Link to={`/accounts/${r.accountId}`} className="underline text-primary">
          {r.accountReferenceNo}
        </Link>
      ),
    },
    { id: "company", header: "Company", accessor: (r) => r.companyName ?? "—" },
    { id: "client",  header: "Client",  accessor: (r) => r.clientFullName ?? "—" },
    {
      id: "status",
      header: "Status",
      accessor: (r) => <AccountStatusBadge code={r.accountStatusCode as AccountStatusCode} />,
    },
    {
      id: "balance",
      header: "Balance",
      accessor: (r) => fmt.format(r.currentBalanceOutstanding),
      align: "right",
    },
    {
      id: "overdue",
      header: "Overdue",
      accessor: (r) => r.totalOverdue != null ? fmt.format(r.totalOverdue) : "—",
      align: "right",
    },
    { id: "lastPayment", header: "Last payment", accessor: (r) => r.lastPaymentDate ? new Date(r.lastPaymentDate).toLocaleDateString("en-AU") : "—" },
    { id: "lastStmt",    header: "Last stmt",    accessor: (r) => r.lastStatementIssuedDate ? new Date(r.lastStatementIssuedDate).toLocaleDateString("en-AU") : "—" },
    { id: "nextDue",     header: "Next due",     accessor: (r) => r.nextPaymentDueDate ? new Date(r.nextPaymentDueDate).toLocaleDateString("en-AU") : "—" },
    { id: "terms",       header: "Terms",        accessor: (r) => r.termsAccepted ? "✓" : "—" },
  ];

  return (
    <ListPage
      title="Accounts"
      primaryAction={
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => exportCsv(data?.items ?? [])}
            disabled={(data?.items ?? []).length === 0}
          >
            Export CSV
          </Button>
          <IntentButton onClick={() => { setCreating(true); setConflictMessage(null); }}>
            New account
          </IntentButton>
        </div>
      }
      filterBar={
        <TooltipProvider>
          <div className="flex flex-wrap gap-2">
            {SAVED_VIEWS.map((v) =>
              v.disabled ? (
                <Tooltip key={v.id}>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        className="cursor-not-allowed opacity-50"
                      >
                        {v.label}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{v.tooltip}</TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  key={v.id}
                  variant={activeView === v.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setActiveView(v.id); setPage(0); }}
                >
                  {v.label}
                </Button>
              )
            )}
          </div>
        </TooltipProvider>
      }
    >
      <DataGrid
        rows={data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.accountId}
        totalRows={data?.totalCount ?? 0}
        pageIndex={page}
        pageSize={50}
        loading={isLoading}
        onPageChange={setPage}
      />
      <DetailsDrawer
        open={creating}
        onOpenChange={(o) => { if (!o) { setCreating(false); setConflictMessage(null); } }}
        title="New account"
      >
        <NewAccountForm
          onSubmit={(req) => createMut.mutate(req)}
          pending={createMut.isPending}
          conflictMessage={conflictMessage}
        />
      </DetailsDrawer>
    </ListPage>
  );
}
